import React, { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Typography, TextField, InputAdornment, Pagination, TablePagination, PaginationItem, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ServiceProviderDetail, { ServiceProviderData } from '../serviceprovider/serviceProviderDetail';
import Download from '../../assets/images/download.png';
import Maximize from '../../assets/images/maximize-2.png';
import swal from 'sweetalert';
import { getfieldstaffsignupdetails } from '../../Models/model';

type GridRow = {
    id: number;
    FullName?: string;
    fieldstaffPrimaryMobile?: string;
    fieldStaffPrimaryEmail?: string;
    currentStatus?: string;
    userAddressdet?: { country?: string }[];
};

const TAB_TO_STATUS: Record<string, string> = {
    New: 'Submitted',
    Accepted: 'Approved',
    Rejected: 'Rejected',
};

function ServiceProvider() {
    const [selected, setSelected] = useState<string>('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [_page, _setPage] = useState(1);
    const [_rowsPerPage, _setRowsPerPage] = useState(10);

    const [view, setView] = useState<'grid' | 'detail'>('grid');
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const [detailData, setDetailData] = useState<ServiceProviderData | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [rows, setRows] = useState<GridRow[]>([]);
    const [gridLoading, setGridLoading] = useState(false);

    const fetchGridData = () => {
        setGridLoading(true);
        getfieldstaffsignupdetails(0)
            .then((response: any) => {
                if (response?.data?.status === 'Success') {
                    setRows(response?.data?.data || []);
                } else {
                    swal(response?.data?.data || 'Failed to load list', { icon: 'warning' });
                }
            })
            .catch((error: any) => {
                console.log(error);
                swal('Something went wrong loading the grid', { icon: 'error' });
            })
            .finally(() => setGridLoading(false));
    };

    useEffect(() => {
        fetchGridData();
    }, []);

    const mapToServiceProviderData = (raw: any): ServiceProviderData => {
        const nameParts = (raw?.FullName || '').trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const address = raw?.userAddressdet?.[0] || {};

        const fileNameFromUrl = (url: string | null | undefined) => {
            if (!url) return '';
            try {
                return decodeURIComponent(url.split('/').pop() || url);
            } catch {
                return url;
            }
        };

        const formatDate = (val: string | null | undefined) => {
            if (!val) return '';
            const d = new Date(val);
            if (isNaN(d.getTime())) return val;
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        };

        const documents = [
            raw?.panNumber && {
                sno: '01',
                name: 'PAN Card',
                number: raw.panNumber,
                fileName: fileNameFromUrl(raw?.panUrl),
                fileUrl: raw?.panUrl || '',
            },
            raw?.aadharNumber && {
                sno: '02',
                name: 'Aadhaar Card',
                number: raw.aadharNumber,
                fileName: '',
                fileUrl: '',
            },
            raw?.dlnumber && {
                sno: '03',
                name: `Driving License (Front) - Expires ${formatDate(raw?.dlexpiryDate)}`,
                number: raw.dlnumber,
                fileName: fileNameFromUrl(raw?.dlfrontUrl),
                fileUrl: raw?.dlfrontUrl || '',
            },
            raw?.dlBackUrl && {
                sno: '04',
                name: 'Driving License (Back)',
                number: raw.dlnumber || '',
                fileName: fileNameFromUrl(raw?.dlBackUrl),
                fileUrl: raw?.dlBackUrl || '',
            },
        ].filter(Boolean) as ServiceProviderData['documents'];

        return {
            currentStatus: raw?.currentStatus || '',
            basicInfo: {
                firstName,
                lastName,
                mobileNo: raw?.fieldstaffPrimaryMobile || '',
                email: raw?.fieldStaffPrimaryEmail || '',
            },
            address: {
                addressLine1: address.addressLine1 || '',
                addressLine2: address.addressLine2 || '',
                country: address.country || '',
                state: address.state || '',
                city: address.city || '',
                pincode: address.postalCode || '',
            },
            selfieUrl: raw?.selfieurl || raw?.profilePicUrl || '',
            documents,
        };
    };

    const handleViewClick = async (row: GridRow) => {
        setSelectedRowId(row.id);
        setDetailLoading(true);
        try {
            const response = await getfieldstaffsignupdetails(row.id);
            const record = response?.data?.data?.[0];
            if (!record) {
                swal('No signup details found for this user', { icon: 'warning' });
                setDetailLoading(false);
                return;
            }
            setDetailData(mapToServiceProviderData(record));
            setView('detail');
        } catch (error) {
            console.log(error);
            swal('Something went wrong fetching applicant details', { icon: 'error' });
        } finally {
            setDetailLoading(false);
        }
    };

    const handleBackToGrid = () => {
        setView('grid');
        setSelectedRowId(null);
        setDetailData(null);
        fetchGridData();
    };

    const handleClick = (value: string) => {
        setSelected(value);
        _setPage(1);
    };

    const filteredRows = rows.filter((row) => {
        const matchesTab = selected === 'All' || row.currentStatus === TAB_TO_STATUS[selected];
        if (!matchesTab) return false;
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            String(row.id).toLowerCase().includes(q) ||
            row.FullName?.toLowerCase().includes(q) ||
            row.fieldstaffPrimaryMobile?.toLowerCase().includes(q) ||
            row.fieldStaffPrimaryEmail?.toLowerCase().includes(q) ||
            row.userAddressdet?.[0]?.country?.toLowerCase().includes(q)
        );
    });

    const totalCount = filteredRows.length;

    const paginatedRows = filteredRows.slice(
        (_page - 1) * _rowsPerPage,
        (_page - 1) * _rowsPerPage + _rowsPerPage
    );

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        _setPage(newPage + 1);
    };

    const handleChangePage1 = (event: React.ChangeEvent<unknown>, value: number) => {
        _setPage(value);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        _setRowsPerPage(newRowsPerPage);
        _setPage(1);
    };

    if (view === 'detail' && detailData) {
        return <ServiceProviderDetail applicant={detailData} onBack={handleBackToGrid} />;
    }

    return (
        <div style={{ minHeight: '100vh' }}>
            <div className="p-3 pb-0 mb-4">
                <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#000' }}>Service Provider</Typography>
            </div>

            <div style={{ paddingLeft: 18, paddingTop: 10 }}>
                <Box display="flex" gap={4} sx={{ borderBottom: '1px solid #e0e0e0', width: '100%' }}>
                    {['All', 'New', 'Accepted', 'Rejected'].map((option) => (
                        <Typography
                            key={option}
                            onClick={() => handleClick(option)}
                            sx={{
                                cursor: 'pointer',
                                paddingBottom: '10px',
                                position: 'relative',
                                color: selected === option ? '#000' : '#9a9da3',
                                fontWeight: selected === option ? 600 : 400,
                                '&::after': selected === option
                                    ? { content: '""', position: 'absolute', bottom: '-1px', left: 0, width: '100%', height: '2px', backgroundColor: '#ff3b30' }
                                    : {},
                            }}
                        >
                            {option}
                        </Typography>
                    ))}
                </Box>
            </div>

            <div className="p-3 content-area mt-1" style={{ marginTop: -10 }}>
                <div className="py-3 gx-0 text-white ps-3 row d-flex align-items-center justify-content-between mb-3" style={{ backgroundColor: '#ffffff' }}>
                    <div className="d-flex align-items-center col-auto">
                        <div className="col-auto">
                            <TextField
                                variant="outlined"
                                autoComplete="off"
                                size="small"
                                className="text-white border border-white white-input"
                                value={searchQuery}
                                onChange={(e) => { setSearchQuery(e.target.value); _setPage(1); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ color: '#b9b5b5' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                placeholder="Search here"
                            />
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-3 col-auto mx-4">
                        <img role="button" className="" src={Maximize} alt="Maximize Icon" draggable="false" style={{ filter: 'brightness(0)' }} />
                        <img role="button" className="" src={Download} alt="Download Icon" draggable="false" style={{ filter: 'brightness(0)' }} />
                    </div>
                </div>

                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap", color: "#353535", fontWeight: "700" }} align="center">Action</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap", color: "#353535", fontWeight: "700" }} align="center">Status</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>First Name</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>Last Name</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>Mobile No</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>Email Address</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#353535', fontWeight: 700 }}>Country</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {gridLoading ? (
                                <TableRow>
                                    <TableCell align="center" colSpan={7}>Loading...</TableCell>
                                </TableRow>
                            ) : filteredRows.length ? (
                                paginatedRows.map((row, index) => (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {String((_page - 1) * _rowsPerPage + index + 1).padStart(2, '0')}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }} align="center">
                                            <Button
                                                variant="contained"
                                                className="bg-success"
                                                style={{ borderRadius: "10px" }}
                                                onClick={() => handleViewClick(row)}
                                                type="submit"
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row?.currentStatus === "Approved" && (
                                                <Button
                                                    variant="contained"
                                                    className="bg-success px-4"
                                                    style={{ borderRadius: "10px" }}
                                                    type="submit"
                                                >
                                                    Approved
                                                </Button>
                                            )}
                                            {row?.currentStatus === "Hold" && (
                                                <Button
                                                    variant="contained"
                                                    className="bg-info text-white px-4  text-nowrap"
                                                    style={{ borderRadius: "10px" }}
                                                    type="submit"
                                                >
                                                    On Hold
                                                </Button>
                                            )}
                                            {row?.currentStatus === "InProgress" && (
                                                <Button
                                                    variant="contained"
                                                    className="bg-primary px-4  "
                                                    style={{ borderRadius: "10px" }}
                                                    type="submit"
                                                >
                                                    InProgress
                                                </Button>
                                            )}

                                            {row?.currentStatus === "Submitted" && (
                                                <Button
                                                    variant="contained"
                                                    className="bg-warning px-4 text-nowrap "
                                                    style={{ borderRadius: "10px" }}
                                                    type="submit"
                                                >
                                                    Submitted
                                                </Button>
                                            )}
                                            {row?.currentStatus === "Rejected" && (
                                                <Button
                                                    variant="contained"
                                                    className="bg-danger px-4 text-nowrap "
                                                    style={{ borderRadius: "10px" }}
                                                    type="submit"
                                                >
                                                    Rejected
                                                </Button>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {row.FullName || ''}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.FullName || ''}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fieldstaffPrimaryMobile || ''}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.fieldStaffPrimaryEmail || ''}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.userAddressdet?.[0]?.country || ''}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell align="center" colSpan={7}>Data Not Found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <div className="d-flex justify-content-between align-items-center mt-3 pb-4">
                    <TablePagination
                        className="proposalPagination"
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={totalCount}
                        rowsPerPage={_rowsPerPage}
                        page={_page - 1}
                        SelectProps={{ inputProps: { roboto: 'rows per page' }, native: true }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelDisplayedRows={() => ''}
                    />

                    <Pagination
                        count={Math.ceil(totalCount / _rowsPerPage) || 1}
                        onChange={handleChangePage1}
                        page={_page}
                        variant="outlined"
                        size="small"
                        color="primary"
                        shape="rounded"
                        renderItem={(item) => <PaginationItem sx={{ mx: '4px' }} {...item} />}
                    />
                </div>
            </div>
        </div>
    );
}

export default ServiceProvider;
